import { Box, Typography, Grid, Paper, TextField, InputAdornment } from "@mui/material";
import errorHandler from "../../utils/errorHandler";
import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { capitalizeFirstLetter } from "../../utils/helpers";
import { gamePointManagementApi } from "../../services/AllServices";
import CardLayout from "../../layouts/CardLayout";
import { Helmet } from "react-helmet-async";
import SearchIcon from '@mui/icons-material/Search';
import { styles } from "./ViewGamePoint.styles";

const roles = ["forward", "midfielder", "defender", "goalkeeper"];

const ViewGamePoint = () => {
  const { id } = useParams();
  const [data, setData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await gamePointManagementApi.getById({ id });
        setData(response?.data?.data);
      } catch (error) {
        errorHandler(error);
      }
    };

    fetchData();
  }, [id]);

  // Filter scoring entries based on search query
  const filteredScoringEntries = useMemo(() => {
    if (!data?.scoring) return [];

    const entries = Object.entries(data.scoring);
    if (!searchQuery.trim()) return entries;

    return entries.filter(([eventKey, eventData]) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        eventData?.eventName?.toLowerCase().includes(searchLower) ||
        eventData?.eventStatsGroup?.toLowerCase().includes(searchLower) ||
        roles.some(role =>
          eventData[role]?.toString().toLowerCase().includes(searchLower)
        )
      );
    });
  }, [data?.scoring, searchQuery]);

  return (
    <>
      <Helmet>
        <title>View Game Point | Squad Duel Admin</title>
      </Helmet>

      <CardLayout>
        <Box sx={styles.headerContainer}>
          <Typography variant="h4" gutterBottom sx={styles.headerTitle}>
            🎯 View Scoring Rules
          </Typography>
          <Typography variant="body1" sx={styles.headerSubtitle}>
            View point values and multipliers for different player roles and events
          </Typography>
        </Box>

        <Paper sx={styles.globalSettingsContainer}>
          <Box sx={styles.globalSettingsHeader}>
            <Box sx={styles.globalSettingsIcon}>
              <Typography sx={styles.globalSettingsIconText}>⚙️</Typography>
            </Box>
            <Box>
              <Typography variant="h6" sx={styles.globalSettingsTitle}>
                Global Settings
              </Typography>
              <Typography variant="body2" sx={styles.globalSettingsSubtitle}>
                View multipliers and contest settings
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ ...styles.fieldCard, ...styles.captainFieldCard }}>
                <Box sx={styles.fieldHeader}>
                  <Typography sx={styles.fieldIcon}>👑</Typography>
                  <Typography variant="subtitle2" sx={styles.captainFieldLabel}>
                    Captain Multiplier
                  </Typography>
                </Box>
                <Box sx={styles.fieldValue}>
                  <Typography variant="h6" sx={styles.fieldValueText}>
                    {data?.captainMultiplier ?? "-"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ ...styles.fieldCard, ...styles.viceCaptainFieldCard }}>
                <Box sx={styles.fieldHeader}>
                  <Typography sx={styles.fieldIcon}>🥈</Typography>
                  <Typography variant="subtitle2" sx={styles.viceCaptainFieldLabel}>
                    Vice Captain Multiplier
                  </Typography>
                </Box>
                <Box sx={styles.fieldValue}>
                  <Typography variant="h6" sx={styles.fieldValueText}>
                    {data?.viceCaptainMultiplier ?? "-"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ ...styles.fieldCard, ...styles.contestTypeFieldCard }}>
                <Box sx={styles.fieldHeader}>
                  <Typography sx={styles.fieldIcon}>🏆</Typography>
                  <Typography variant="subtitle2" sx={styles.contestTypeFieldLabel}>
                    Contest Type
                  </Typography>
                </Box>
                <Box sx={styles.fieldValue}>
                  <Typography variant="h6" sx={styles.fieldValueText}>
                    {capitalizeFirstLetter(data?.contestType) ?? "-"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Search Input */}
        <Box sx={styles.searchContainer}>
          <Typography variant="h6" sx={styles.searchTitle}>
            🔍 Search Scoring Events
          </Typography>
          <TextField
            fullWidth
            placeholder="Search by event name, stats group, or point values..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'primary.main' }} />
                </InputAdornment>
              ),
            }}
            size="medium"
            sx={styles.searchInput}
          />
          {searchQuery && (
            <Box sx={styles.searchResults}>
              <Typography variant="body2" sx={styles.searchResultsText}>
                ✨ Found {filteredScoringEntries.length} matching entries
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={styles.tableHeader}>
          <Box sx={{ width: '20%', ...styles.tableHeaderCell }}>
            <strong>Event Name</strong>
          </Box>
          <Box sx={{ width: '15%', ...styles.tableHeaderCell }}>
            <strong>Stats Group</strong>
          </Box>
          {roles.map((role) => (
            <Box key={role} sx={{ width: '12%', ...styles.tableHeaderCell }}>
              <strong>{role.charAt(0).toUpperCase() + role.slice(1)}</strong>
            </Box>
          ))}
          <Box sx={{ width: '15%', ...styles.tableHeaderCell }}>
            <strong>Status</strong>
          </Box>
        </Box>

        <Paper sx={styles.tableContainer}>
          {filteredScoringEntries.length === 0 ? (
            <Box sx={styles.emptyStateContainer}>
              <Typography variant="h6" sx={styles.emptyStateTitle}>
                {searchQuery ? '🔍 No matching entries found' : '📊 No scoring entries available'}
              </Typography>
              <Typography variant="body2" sx={styles.emptyStateText}>
                {searchQuery ? 'Try adjusting your search terms or clearing the search.' : 'No scoring events have been configured yet.'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: 1400, overflow: 'auto', overflowX: 'hidden' }}>
              {filteredScoringEntries.map(([eventKey, rolesData], index) => (
                <Grid
                  key={eventKey}
                  container
                  spacing={1}
                  sx={{
                    ...styles.rowContainer,
                    height: 70
                  }}
                >
                  <Grid item xs={2.5}>
                    <Typography sx={styles.eventNameText}>
                      {rolesData?.eventName}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Box sx={styles.statsGroupContainer}>
                      <Typography sx={styles.statsGroupText}>
                        {rolesData?.eventStatsGroup}
                      </Typography>
                    </Box>
                  </Grid>
                  {roles.map((role) => (
                    <Grid item xs={1.5} key={role}>
                      <Box sx={styles.roleValueBox}>
                        <Typography sx={styles.roleValueText}>
                          {rolesData[role] ?? "-"}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                  <Grid item xs={1.5}>
                    <Box sx={styles.statusContainer}>
                      <Typography sx={{
                        ...styles.statusText,
                        ...(rolesData?.status === 1 ? styles.activeStatusText : styles.inactiveStatusText)
                      }}>
                        {rolesData?.status === 1 ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              ))}
            </Box>
          )}
        </Paper>
      </CardLayout>
    </>
  );
};

export default ViewGamePoint;
